// BottomChartPanel.jsx
import React, { useMemo, useState, useEffect, useId } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import {
  FileDownload,
  DateRange,
  CompareArrows,
  OpenInFull,
  CloseFullscreen,
} from "@mui/icons-material";
import { downloadTimeSeriesCsv } from "../utils/downloadTimeSeries";
import "./BottomChartPanel.css";

const BottomChartPanel = ({
  data = [],
  productConfig,
  activeIndicator,
  onExpandChange,
  onOpenTimeFilter,
}) => {
  const [isExpanded, setIsExpanded] = useState(true);
  const gradientId = useId().replace(/:/g, "");

  useEffect(() => {
    onExpandChange?.(isExpanded);
  }, [isExpanded, onExpandChange]);

  const yDomain = useMemo(() => {
    // If product config provides min/max, honor it. Otherwise compute from data with padding.
    if (productConfig && productConfig.minValue != null && productConfig.maxValue != null) {
      return [productConfig.minValue, productConfig.maxValue];
    }
    const values = data
      .map((d) => (typeof d.value === "number" ? d.value : Number(d.value)))
      .filter((v) => Number.isFinite(v));
    if (!values.length) return [0, 1];
    const min = Math.min(...values);
    const max = Math.max(...values);
    const span = max - min;
    const pad = span > 0 ? span * 0.1 : Math.max(1, Math.abs(max) * 0.1);
    const low = Number((min - pad).toFixed(3));
    const high = Number((max + pad).toFixed(3));
    return [low, high];
  }, [productConfig]);

  const chartTitle = useMemo(() => {
    if (activeIndicator) {
      return `نمودار تغییرات — ${activeIndicator}`;
    }
    return "نمودار سری زمانی";
  }, [activeIndicator]);

  const unitLabel = productConfig?.unit ? ` (${productConfig.unit})` : "";

  const { average, latest, peak } = useMemo(() => {
    if (!data.length) return { average: "—", latest: "—", peak: "—" };
    const values = data
      .map((d) => (typeof d.value === "number" ? d.value : Number(d.value)))
      .filter((v) => Number.isFinite(v));
    if (!values.length) return { average: "—", latest: "—", peak: "—" };
    const sum = values.reduce((a, b) => a + b, 0);
    return {
      average: (sum / values.length).toFixed(2),
      latest: values[values.length - 1].toFixed(2),
      peak: Math.max(...values).toFixed(2),
    };
  }, [data]);

  const toggleExpand = () => setIsExpanded((prev) => !prev);

  const handleDownload = () => {
    const type = productConfig?.productType || "series";
    downloadTimeSeriesCsv(data, `timeseries-${type}.csv`);
  };

  return (
    <div className={`bottom-panel ${!isExpanded ? "collapsed" : ""}`}>
      <div className="panel-header">
        <div className="panel-title">
          <span
            className="expand-icon"
            onClick={toggleExpand}
            role="button"
            tabIndex={0}
          >
            {isExpanded ? <CloseFullscreen /> : <OpenInFull />}
          </span>
          {chartTitle}
        </div>
        {isExpanded && (
          <div className="panel-controls">
            <button
              className="btn-outline"
              type="button"
              disabled
              title="به‌زودی"
            >
              <CompareArrows fontSize="small" /> مقایسه
            </button>
            <button
              className="btn-outline"
              type="button"
              onClick={() => onOpenTimeFilter?.()}
            >
              <DateRange fontSize="small" /> بازه زمانی
            </button>
            <button
              className="btn-download"
              type="button"
              title="دانلود CSV"
              onClick={handleDownload}
              disabled={!data.length}
            >
              <FileDownload />
            </button>
          </div>
        )}
      </div>

      {isExpanded && (
        <div className="panel-content">
          <div className="metrics-sidebar">
            <div className="metric-card">
              <span className="metric-label">آخرین مقدار{unitLabel}</span>
              <span className="metric-value">{latest}</span>
            </div>
            <div className="metric-card">
              <span className="metric-label">میانگین دوره</span>
              <span className="metric-value">{average}</span>
            </div>
            <div className="metric-card highlight">
              <span className="metric-label">حداکثر</span>
              <span className="metric-value">{peak}</span>
            </div>
          </div>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={data}
                margin={{ top: 10, right: 10, left: -20, bottom: 0 }}
              >
                <defs>
                  <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#344E41" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#344E41" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="#eee"
                />
                <XAxis
                  dataKey="label"
                  tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  tick={{ fontFamily: "Yekan Bakh FaNum", fontSize: 12 }}
                  axisLine={false}
                  tickLine={false}
                  domain={yDomain}
                />
                <Tooltip
                  contentStyle={{
                    direction: "rtl",
                    fontFamily: "Yekan Bakh FaNum",
                    borderRadius: "8px",
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value) => [
                    value != null ? value : "—",
                    productConfig?.unit || "مقدار",
                  ]}
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#344E41"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill={`url(#${gradientId})`}
                  connectNulls
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}
    </div>
  );
};

export default BottomChartPanel;
